import { Heading, VStack, Box, Table, Tbody, Tr, Td, 
  useToast, Text, useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon, } from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tooltip } from "@chakra-ui/react";
import { FiEdit, FiCheck, FiX } from "react-icons/fi";
import { jwtDecode } from "jwt-decode";
import UserUpdateTable from '../Components/UserUpdateTable';

interface Tenant {
  tenant_id: number;
  name: string;
  description: string;
  is_active: boolean;
}

interface Subscription {
  subscription_id: number;
  subscription_name: string;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  role_limits:RoleLimits;
}

interface RoleLimits {
    admin_user_count: number;
    tutor_user_count: number;
    student_user_count: number;
  }

interface TenantUsersCount{
  tenant_id: number;
  total_admins : number;
  total_tutors : number;
  total_students : number;
  active_admins : number;
  active_tutors: number;
  active_students : number;
}

interface AdminUser{
    user_id : number;
    username : string;
    email : string;
    password : string;
    tenant_id : number;
    role_id:number;
    is_active : boolean;
}

// Define the expected structure of the token payload
interface TokenPayload {
  sub: string;       // Typically the username or user ID
  role_id: number;   // Role ID
  tenant_id: number; // Tenant ID
  exp?: number;      // Expiration timestamp (optional)
}


const UserManagement: React.FC = () => {
 
  const [selectedTenant, setSelectedTenant] = useState<number | null>(null);
  const [subscription, setSubscription] = useState<Subscription>();
  const [tenantUsersCount, setTenantUsersCount] = useState<TenantUsersCount>();

  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [editRow, setEditRow] = useState<number | null>(null); // Track the row currently being edited

  const toast = useToast();

  const tableHeaderbg = useColorModeValue("gray.200", "gray.700");

  const buttonBg = useColorModeValue(
    'linear-gradient(to right, green.50, green.100)',
    'linear-gradient(to right, green.600, green.700)'
  );
  const buttonHoverBg = useColorModeValue(
    'green.200',
    'green.500'
  );


  // Function to get tenant_id from the token
  const getTenantIdFromToken = (token: string): number | null => {
    try {
      const decoded: TokenPayload = jwtDecode<TokenPayload>(token);
      return decoded.tenant_id || null;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  // Set tenant ID on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const tenantId = getTenantIdFromToken(token);
      console.log("Tenant ID:", tenantId);
      setSelectedTenant(tenantId);
    }
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      const fetchSubscription = async () => {
        try {
          const tenantsapiurl = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
          const response = await axios.get(tenantsapiurl + `/${selectedTenant}/active-subscription`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          });
          setSubscription(response.data);
        } catch (error) {
          console.error("Failed to load Subscription", error);
        }
      };
      fetchSubscription();

      const fetchTenantUsersCount = async () => {
        try {
          const tenantsapiurl = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
          const response = await axios.get(tenantsapiurl + `/${selectedTenant}/user-roles`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          });
          setTenantUsersCount(response.data);
        } catch (error) {
          console.error("Failed to load Tenants Users Count by Roles", error);
        }
      };
      fetchTenantUsersCount();

      fetchAdminUsers();
    }
  }, [selectedTenant]);

  const fetchAdminUsers = async () => {
    try {
        const tenantsapiurl = process.env.REACT_APP_TENANTS_API_URL || 'https://default-url.com';
        const response = await axios.get(tenantsapiurl + `/${selectedTenant}/admin-users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAdminUsers(response.data);
    } catch (error) {
      console.error("Failed to load admin users", error);
    }
  };


  const handleUpdateAdmin = async (adminuser: AdminUser) => {

    try {
        if (selectedTenant) {
            adminuser.tenant_id = selectedTenant
            }
            adminuser.role_id=2;
    
            const payload = {
                username: adminuser.username,
                email: adminuser.email,
                password: adminuser.password,
                tenant_id: selectedTenant || adminuser.tenant_id,
                role_id: 2,
                is_active: adminuser.is_active,
            };
          const apiurl = process.env.REACT_APP_API_URL || 'https://default-url.com';
          
          console.log("printing admin user payload",payload);

          console.log("printing admin user id",adminuser.user_id);
     
      console.log("Printing Token from Update" + localStorage.getItem("token"));
      await axios.put(apiurl+`/update-user/${adminuser.user_id}`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      fetchAdminUsers();
      setEditRow(null); // Reset edit mode after successful update
      toast({ title: "Tenant updated successfully", status: "success" });
    } catch (error) {
      console.error("Failed to update tenant", error);
      toast({ title: "Failed to update tenant", status: "error" });
    }
  };

  const handleEditRow = (user_id: number) => {
    setEditRow(user_id);

  };

  const handleCancelEdit = () => {
    setEditRow(null);

  };

  return (
    <Box as="main" mx="auto" p={10} >
      <VStack>
        <Heading size={"md"}>Admin Accounts Manager</Heading>
      </VStack>
  
        <Box p={4} borderColor={"gray.300"} mt={4} borderWidth={1} borderRadius="sm" mb={4}>
                <Text fontSize="lg" fontWeight="bold" mb={2}>
                   Active Subscription Details for the Tenant
                </Text>
                {subscription? (
                    <Table variant="simple" size="sm">
                        <Tbody>
                            <Tr>
                                <Td fontWeight="bold">Subscription Name:</Td>
                                <Td>{subscription.subscription_name}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Subscription Start Date:</Td>
                                <Td>{subscription.start_date ? new Date(subscription.start_date).toLocaleDateString() : "N/A"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Subscription End Date:</Td>
                                <Td>{subscription.end_date ? new Date(subscription.end_date).toLocaleDateString() : "N/A"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Subscription Status</Td>
                                <Td>{subscription.is_active ? 'Active' : 'Inactive'}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Subscribed Admin User Count</Td>
                                <Td>{subscription.role_limits.admin_user_count}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Subscribed Tutor User Count</Td>
                                <Td> {subscription.role_limits.tutor_user_count}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Subscribed Student User Count</Td>
                                <Td>{subscription.role_limits.student_user_count}</Td>
                            </Tr>
                        </Tbody>
                    </Table>
                ) : (
                    <Text>Loading metadata...</Text>
                )}
          </Box>

          <Box p={4} borderColor={"gray.300"} borderWidth={1} borderRadius="sm" mb={4}>
                <Text fontSize="lg" fontWeight="bold" mb={2}>
                Users Count By Roles for the Tenant
                </Text>
                {tenantUsersCount? (
                    <Table variant="simple" size="sm">
                        <Tbody>
                            <Tr>
                                <Td fontWeight="bold">User Type</Td>
                                <Td fontWeight="bold">Total Created Users </Td>
                                <Td fontWeight="bold">Active Users</Td>
                            </Tr>
                            <Tr>
                                <Td >Admin</Td>
                                <Td>{tenantUsersCount?.total_admins}</Td>
                                <Td>{tenantUsersCount?.active_admins}</Td>
                            </Tr>
                            <Tr>
                                <Td >Tutor</Td>
                                <Td>{tenantUsersCount?.total_tutors}</Td>
                                <Td>{tenantUsersCount?.active_tutors}</Td>
                            </Tr>
                            <Tr>
                                <Td >Student</Td>
                                <Td>{tenantUsersCount?.total_students}</Td>
                                <Td>{tenantUsersCount?.active_students}</Td>
                            </Tr>
                           
                        </Tbody>
                    </Table>
                ) : (
                    <Text>Loading metadata...</Text>
                )}
          </Box>

          <>
      {selectedTenant !== null && (
        <Accordion allowToggle>
          {/* Admin Users */}
          <AccordionItem>
          <AccordionButton
          height="50px"
          backgroundImage={buttonBg} // Use backgroundImage for gradients
          _hover={{ bg: buttonHoverBg }}
          borderRadius="sm"
          mt={2}
          >
            <Box flex="1" textAlign="left" fontSize={"sm"}>
             Admin Users
            </Box>
            <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <UserUpdateTable tenant_id={selectedTenant} role_id={2} />
            </AccordionPanel>
          </AccordionItem>

          {/* Tutor Users */}
          <AccordionItem>
          <AccordionButton
          height="50px"
          backgroundImage={buttonBg} // Use backgroundImage for gradients
          _hover={{ bg: buttonHoverBg }}
          borderRadius="sm"
          mt={2}
          >
            <Box flex="1" textAlign="left" fontSize={"sm"}>
             Tutor Users
            </Box>
            <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <UserUpdateTable tenant_id={selectedTenant} role_id={3} />
            </AccordionPanel>
          </AccordionItem>

          {/* Student Users */}
          <AccordionItem>
          <AccordionButton
          height="50px"
          backgroundImage={buttonBg} // Use backgroundImage for gradients
          _hover={{ bg: buttonHoverBg }}
          borderRadius="sm"
          mt={2}
          >
            <Box flex="1" textAlign="left" fontSize={"sm"}>
             Student Users
            </Box>
            <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <UserUpdateTable tenant_id={selectedTenant} role_id={4} />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </>

       
    </Box>
  );
};

export default UserManagement;
